import { useState, useCallback, useEffect } from 'react';
import { apiFetch, useApi } from '../hooks/useApi.js';
import { OrderFilesView } from './OrderImports.jsx';
import { OrderConsignments } from './Consignments.jsx';
import { OrderLinesEditor, reprice } from './SalesOrders.jsx';
import { downloadSampleDpdFile } from '../components/shared/SampleParcel.jsx';
import { uiConfirm, uiPrompt } from '../utils/dialogs.js';

// ── Primitives (mirrors SalesOrders.jsx conventions) ────────────────────────────
function Btn({ children, variant = 'primary', size = 'md', ...props }) {
  const base = { borderRadius: 'var(--radius)', fontWeight: 600, cursor: 'pointer', border: 'none', transition: 'opacity .15s', fontSize: size === 'sm' ? 12 : 14, padding: size === 'sm' ? '5px 12px' : '9px 18px', opacity: props.disabled ? 0.5 : 1 };
  const v = { primary: { background: 'var(--pld-green)', color: '#fff' }, ghost: { background: 'var(--surface-2)', color: 'var(--text)', border: '1px solid var(--border)' }, danger: { background: '#dc2626', color: '#fff' } };
  return <button {...props} style={{ ...base, ...(v[variant] || v.primary), ...props.style }}>{children}</button>;
}

function Modal({ title, onClose, width = 600, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={onClose}>
      <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', width: '100%', maxWidth: width, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: 'var(--text-muted)', lineHeight: 1 }}>&#215;</button>
        </div>
        <div style={{ padding: 24 }}>{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 14 }}><label style={{ fontSize: 13, fontWeight: 500 }}>{label}</label>{children}</div>;
}
function Input(props) { return <input {...props} style={{ padding: '9px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', color: 'var(--text)', ...props.style }} />; }
function Select({ children, ...props }) { return <select {...props} style={{ padding: '9px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', color: 'var(--text)' }}>{children}</select>; }
function Textarea(props) { return <textarea {...props} style={{ padding: '9px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical', color: 'var(--text)', ...props.style }} />; }
function BtnRow({ children }) { return <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>{children}</div>; }
function Grid2({ children }) { return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>{children}</div>; }
function ErrBanner({ msg }) { return msg ? <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{msg}</p> : null; }

const STATUS_C = {
  DRAFT: { bg: '#f3f4f6', color: '#6b7280' }, REVIEWING: { bg: '#fef9c3', color: '#854d0e' },
  CONFIRMED: { bg: '#dbeafe', color: '#1e40af' }, PICKING: { bg: '#fef9c3', color: '#854d0e' },
  DISPATCHED: { bg: '#dcfce7', color: '#166534' }, DELIVERED: { bg: '#dcfce7', color: '#166534' },
  CANCELLED: { bg: '#fee2e2', color: '#991b1b' },
  UPLOADING: { bg: '#f3f4f6', color: '#6b7280' }, PARSING: { bg: '#fef9c3', color: '#854d0e' },
  PROCESSING: { bg: '#fef9c3', color: '#854d0e' }, COMPLETE: { bg: '#dcfce7', color: '#166534' },
  FAILED: { bg: '#fee2e2', color: '#991b1b' },
  PENDING: { bg: '#f3f4f6', color: '#6b7280' }, PARSED: { bg: '#dbeafe', color: '#1e40af' },
  NEEDS_REVIEW: { bg: '#fef9c3', color: '#854d0e' }, REJECTED: { bg: '#fee2e2', color: '#991b1b' },
  VALID: { bg: '#dcfce7', color: '#166534' }, FLAGGED: { bg: '#fef9c3', color: '#854d0e' },
  APPROVED: { bg: '#dbeafe', color: '#1e40af' }, PROCESSED: { bg: '#dcfce7', color: '#166534' },
};

function StatusBadge({ status }) {
  const c = STATUS_C[status] || { bg: '#f3f4f6', color: '#6b7280' };
  return <span style={{ padding: '3px 9px', borderRadius: 99, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', background: c.bg, color: c.color, whiteSpace: 'nowrap' }}>{status}</span>;
}

function fmt(d) { return d ? new Date(d).toLocaleDateString('en-GB') : '—'; }
function fmtDateTime(d) { return d ? new Date(d).toLocaleString('en-GB') : '—'; }
function fmtCurrency(v) { return v != null ? `£${Number(v).toFixed(2)}` : '—'; }
function pct(v) { return v != null ? `${Math.round(Number(v) * 100)}%` : '—'; }

function TableShell({ headers, children, empty }) {
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: 'var(--surface-2)' }}>
            {headers.map(h => (
              <th key={h} style={{ textAlign: 'left', padding: '9px 14px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
      {empty}
    </div>
  );
}

function EmptyState({ title, sub }) {
  return (
    <div style={{ padding: '48px 24px', textAlign: 'center', color: 'var(--text-muted)', background: 'var(--surface-2)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)' }}>
      <p style={{ fontSize: 15, fontWeight: 600, margin: '0 0 6px' }}>{title}</p>
      {sub && <p style={{ fontSize: 13, margin: 0 }}>{sub}</p>}
    </div>
  );
}

function ClickRow({ onClick, children }) {
  return (
    <tr onClick={onClick} style={{ borderTop: '1px solid var(--border)', cursor: onClick ? 'pointer' : 'default' }}
      onMouseEnter={e => onClick && (e.currentTarget.style.background = 'var(--surface-2)')}
      onMouseLeave={e => onClick && (e.currentTarget.style.background = 'transparent')}>
      {children}
    </tr>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// VIEW 1 — Fulfilment Orders
// ═════════════════════════════════════════════════════════════════════════════

const FO_STATUSES = ['DRAFT', 'REVIEWING', 'CONFIRMED', 'PICKING', 'DISPATCHED', 'DELIVERED', 'CANCELLED'];

// Open a PDF from Blake in a new tab (packing slips, Joe 27 Oct 2026).
async function openPdf(path) {
  const tab = window.open('', '_blank');
  const res = await fetch(`/api${path}`, { headers: { Authorization: `Bearer ${localStorage.getItem('pld_token') || ''}` } });
  if (!res.ok) { tab?.close(); let m = `HTTP ${res.status}`; try { m = (await res.json()).error || m; } catch { /* not json */ } throw new Error(m); }
  const url = URL.createObjectURL(await res.blob());
  if (tab) tab.location.href = url; else window.location.href = url;
}

export function FulfilmentOrderDetail({ orderId, onBack, onRefresh, clientsById }) {
  const { data, loading, refetch } = useApi(`/orders/fulfilment/${orderId}`, [orderId]);
  const order = data?.data ?? data ?? null;
  const [acting, setActing] = useState(false);
  const [err, setErr] = useState('');

  async function confirmOrder() {
    if (!(await uiConfirm('Confirm this order? Client stock will be reserved and charges calculated.'))) return;
    setActing(true); setErr('');
    try { await apiFetch(`/orders/fulfilment/${orderId}/confirm`, { method: 'POST', body: {} }); refetch(); onRefresh(); }
    catch (e) { setErr(e.message); } finally { setActing(false); }
  }

  async function despatchOrder() {
    if (!(await uiConfirm('Mark this order as despatched? The stock comes off Blake now, duty is recorded for anything leaving bond, and the order goes on this month\'s bill for the client.'))) return;
    setActing(true); setErr('');
    try { await apiFetch(`/orders/fulfilment/${orderId}/despatch`, { method: 'POST', body: {} }); refetch(); onRefresh(); }
    catch (e) { setErr(e.message); } finally { setActing(false); }
  }

  async function cancelOrder() {
    const reason = (await uiPrompt('Reason for cancellation:'));
    if (reason === null) return;
    setActing(true); setErr('');
    try { await apiFetch(`/orders/fulfilment/${orderId}/cancel`, { method: 'POST', body: { reason } }); refetch(); onRefresh(); }
    catch (e) { setErr(e.message); } finally { setActing(false); }
  }

  if (loading || !order) return <div style={{ padding: 40, color: 'var(--text-muted)' }}>Loading...</div>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 24 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 20, lineHeight: 1, padding: 0, marginTop: 4 }}>&#8592;</button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, fontFamily: 'monospace' }}>{order.order_ref}</h2>
            <StatusBadge status={order.status} />
          </div>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
            {order.client_name || clientsById[order.client_id] || order.client_id} · {order.source} · {order.delivery_method?.replace(/_/g, ' ')} · Created {fmt(order.created_at)}{order.dispatched_at ? ` · Despatched ${fmtDateTime(order.dispatched_at)}` : ''}{order.fulfilment_invoice_id ? ' · Billed' : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {order.has_packing_slip && <Btn size="sm" variant="ghost" onClick={() => openPdf(`/orders/fulfilment/${orderId}/packing-slip.pdf`).catch((e) => setErr(e.message))}>📄 Packing slip</Btn>}
          {(order.status === 'DRAFT' || order.status === 'REVIEWING') && (
            <>
              <Btn size="sm" onClick={confirmOrder} disabled={acting}>Confirm Order</Btn>
              <Btn size="sm" variant="danger" onClick={cancelOrder} disabled={acting}>Cancel</Btn>
            </>
          )}
          {['CONFIRMED', 'PICKING'].includes(order.status) && (
            <>
              <Btn size="sm" onClick={despatchOrder} disabled={acting}>Mark despatched</Btn>
              <Btn size="sm" variant="danger" onClick={cancelOrder} disabled={acting}>Cancel</Btn>
            </>
          )}
        </div>
      </div>
      <ErrBanner msg={err} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10, marginBottom: 24 }}>
        {[
          { label: 'Recipient', value: order.recipient_name || '—' },
          { label: 'Delivery address', value: [order.recipient_address_line1, order.recipient_city, order.recipient_postcode].filter(Boolean).join(', ') || '—' },
          { label: 'Delivery method', value: order.delivery_method?.replace(/_/g, ' ') || '—' },
          { label: 'Charge to client', value: fmtCurrency(order.charge_total) },
        ].map(({ label, value }) => (
          <div key={label} style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '12px 14px' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{value}</div>
          </div>
        ))}
      </div>

      {(order.charge_total != null) && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
          {[
            ['Duty', order.charge_duty], ['Packaging', order.charge_packaging],
            ['Shipping', order.charge_shipping], ['Handling', order.charge_handling],
          ].map(([label, val]) => (
            <div key={label} style={{ padding: '8px 14px', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: 12 }}>
              <span style={{ color: 'var(--text-muted)' }}>{label}: </span><strong>{fmtCurrency(val)}</strong>
            </div>
          ))}
        </div>
      )}

      {order.notes && <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '12px 16px', fontSize: 13, color: 'var(--text-muted)', marginBottom: 20 }}>{order.notes}</div>}

      <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Order lines</h3>
      {!order.lines?.length ? <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>No lines.</p> : (
        <TableShell headers={['Product', 'SKU', 'Qty', order.status === 'DISPATCHED' ? 'Came from' : 'Reserved at', 'LPA', 'Duty']}>
          {order.lines.map((l, i) => {
            const picks = (order.picks || []).filter(p => p.line_id === l.id);
            return (
              <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                <td style={{ padding: '10px 14px', fontWeight: 600 }}>{l.stock_item_name || l.stock_item_id}</td>
                <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontSize: 12, color: 'var(--text-muted)' }}>{l.sku}</td>
                <td style={{ padding: '10px 14px' }}>{l.quantity_units}</td>
                <td style={{ padding: '10px 14px', fontSize: 12 }}>
                  {picks.length ? picks.map((p, j) => (
                    <div key={j}>{Number(p.quantity)} × <code>{p.location_code}</code> <span style={{ color: 'var(--text-muted)' }}>{p.duty_status === 'in_bond' ? 'in bond' : p.duty_status === 'duty_paid' ? 'duty paid' : ''}{p.duty_ref ? ` · ${p.duty_ref}` : ''}</span></div>
                  )) : '—'}
                </td>
                <td style={{ padding: '10px 14px' }}>{Number(l.lpa) ? Number(l.lpa).toFixed(3) : '—'}</td>
                <td style={{ padding: '10px 14px' }}>{Number(l.duty_amount) ? fmtCurrency(l.duty_amount) : '—'}</td>
              </tr>
            );
          })}
        </TableShell>
      )}
      <OrderConsignments kind="fulfilment_order" orderId={orderId} />
    </div>
  );
}

const FO_FILTERS = [
  { key: 'todo', label: 'To confirm', status: 'DRAFT,REVIEWING' },
  { key: 'ready', label: 'Ready to despatch', status: 'CONFIRMED,PICKING' },
  { key: 'despatched', label: 'Despatched', status: 'DISPATCHED,DELIVERED' },
  { key: 'cancelled', label: 'Cancelled', status: 'CANCELLED' },
  { key: 'all', label: 'All', status: '' },
];

function FulfilmentOrdersView() {
  const [filter, setFilter] = useState('todo');
  const [clientFilter, setClientFilter] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [picked, setPicked] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const { data: clientData } = useApi('/clients');
  const clients = clientData?.clients ?? clientData?.data ?? clientData ?? [];
  const clientsById = Object.fromEntries((Array.isArray(clients) ? clients : []).map(c => [c.id, c.name]));

  const f = FO_FILTERS.find(x => x.key === filter);
  const qs = new URLSearchParams({ limit: '500', ...(f.status && { status: f.status }), ...(clientFilter && { client_id: clientFilter }) }).toString();
  const { data, loading, refetch } = useApi(`/orders/fulfilment?${qs}`, [filter, clientFilter]);
  const orders = data?.data ?? data ?? [];

  useEffect(() => { setPicked(new Set()); }, [filter, clientFilter]);
  // Samples waiting to go by DPD (queued when sample labels are printed).
  const { data: spData, refetch: refetchSamples } = useApi('/sample-parcels');
  const samples = Array.isArray(spData) ? spData : spData?.data ?? [];

  if (selectedId) return <FulfilmentOrderDetail orderId={selectedId} onBack={() => setSelectedId(null)} onRefresh={refetch} clientsById={clientsById} />;

  const allPicked = orders.length > 0 && orders.every(o => picked.has(o.id));
  const toggle = (id) => setPicked(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setPicked(allPicked ? new Set() : new Set(orders.map(o => o.id)));
  const chosen = orders.filter(o => picked.has(o.id));
  const canConfirm = chosen.length && chosen.every(o => ['DRAFT', 'REVIEWING'].includes(o.status));
  const canDespatch = chosen.length && chosen.every(o => ['CONFIRMED', 'PICKING'].includes(o.status));
  const canCancel = chosen.length && chosen.every(o => !['DISPATCHED', 'DELIVERED', 'CANCELLED'].includes(o.status));

  // Picking list (PDF) / DPD import file (CSV) for the selected orders.
  async function makeFile(kind) {
    setBusy(true); setResult(null);
    const path = { pick: '/api/orders/fulfilment/picking-list.pdf', slips: '/api/orders/fulfilment/packing-slips.pdf', dpd: '/api/orders/fulfilment/dpd-export.csv' }[kind];
    const isPdf = kind !== 'dpd';
    const label = { pick: 'Picking list', slips: 'Packing slips', dpd: 'DPD file' }[kind];
    // Open the tab first (popup blockers allow it only straight from the click).
    const win = isPdf ? window.open('', '_blank') : null;
    try {
      const res = await fetch(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ ids: chosen.map(o => o.id) }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed (HTTP ${res.status})`);
      }
      const blob = await res.blob();
      const name = ((res.headers.get('Content-Disposition') || '').match(/filename="([^"]+)"/) || [])[1] || 'dpd-import.csv';
      const url = URL.createObjectURL(blob);
      let notes = [];
      try { notes = JSON.parse(decodeURIComponent(res.headers.get('X-Blake-Notes') || '%5B%5D')); } catch { /* ignore */ }
      if (isPdf) {
        if (win) win.location.href = url; else window.open(url, '_blank');
        const n = kind === 'slips' ? Number(res.headers.get('X-Blake-Count') || 0) : chosen.length;
        setResult({ label, info: `${label} for ${n} order${n === 1 ? '' : 's'} opened in a new tab.`, notes });
      } else {
        const a = document.createElement('a'); a.href = url; a.download = name; a.click();
        setResult({ label: 'DPD file', info: `${name} downloaded — ${res.headers.get('X-Blake-Count') || chosen.length} shipment(s). Upload it in MyDPD Local.`, notes });
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      refetch(); refetchSamples();
    } catch (e) {
      if (win) win.close();
      setResult({ label, error: e.message });
    } finally { setBusy(false); }
  }

  async function run(action, label) {
    let reason;
    if (action === 'cancel') { reason = (await uiPrompt(`Cancel ${chosen.length} order(s)? Reason:`)); if (reason === null) return; }
    else if (!(await uiConfirm(`${label} ${chosen.length} order${chosen.length === 1 ? '' : 's'}?${action.includes('despatch') ? '\n\nStock comes off Blake now, duty is recorded for anything leaving bond, and the orders go on this month\'s bill.' : ''}`))) return;
    setBusy(true); setResult(null);
    try {
      const res = await apiFetch('/orders/fulfilment/bulk', { method: 'POST', body: { action, ids: chosen.map(o => o.id), reason } });
      setResult({ label, ...res.data });
      setPicked(new Set());
      refetch();
    } catch (e) { setResult({ label, error: e.message }); } finally { setBusy(false); }
  }

  const td = { padding: '10px 12px' };
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {FO_FILTERS.map(x => (
            <button key={x.key} onClick={() => setFilter(x.key)} style={{ padding: '5px 12px', borderRadius: 99, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none', background: filter === x.key ? 'var(--pld-green)' : 'var(--surface-2)', color: filter === x.key ? '#fff' : 'var(--text-muted)' }}>{x.label}</button>
          ))}
        </div>
        <div style={{ marginLeft: 'auto', width: 220 }}>
          <Select value={clientFilter} onChange={e => setClientFilter(e.target.value)}>
            <option value="">All clients</option>
            {(Array.isArray(clients) ? clients : []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
        </div>
      </div>

      {samples.length > 0 && (
        <div style={{ border: '1px solid #bfdbfe', background: '#eff6ff', borderRadius: 'var(--radius)', padding: '10px 14px', marginBottom: 12, fontSize: 13, color: '#1e3a8a' }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <strong>{samples.length} sample parcel{samples.length === 1 ? '' : 's'} waiting for DPD</strong>
            <span style={{ color: '#1e40af' }}>— they go in the next DPD file you download, or on their own:</span>
            <Btn size="sm" variant="ghost" disabled={busy} onClick={async () => {
              setBusy(true); setResult(null);
              try { const n = await downloadSampleDpdFile(); setResult({ label: 'DPD file', info: `Sample DPD file downloaded — ${n} parcel${n === 1 ? '' : 's'}. Upload it in MyDPD Local.` }); }
              catch (e) { setResult({ label: 'DPD file', error: e.message }); }
              finally { setBusy(false); refetchSamples(); }
            }}>⬇ DPD file for samples</Btn>
          </div>
          {samples.map((p) => (
            <div key={p.id} style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
              <span>• {p.recipient_company || p.recipient_name}, {p.postcode} — {p.kind === 'production' ? `run ${p.run_ref || ''}` : `${p.project_ref || p.project_name || 'development'}${p.round_number ? ` round ${p.round_number}` : ''}`} · {p.parcels} parcel{p.parcels === 1 ? '' : 's'}, {Number(p.weight_kg)} kg</span>
              <button type="button" onClick={async () => {
                if (!(await uiConfirm(`Don't send the sample parcel to ${p.recipient_company || p.recipient_name}?`))) return;
                try { await apiFetch(`/sample-parcels/${p.id}`, { method: 'DELETE' }); refetchSamples(); } catch (e) { window.alert(e.message); }
              }} style={{ background: 'none', border: 'none', color: '#1e40af', textDecoration: 'underline', cursor: 'pointer', fontSize: 12 }}>don&rsquo;t send</button>
            </div>
          ))}
        </div>
      )}

      {result && (
        <div style={{ background: result.error || result.failed ? '#fef3c7' : '#dcfce7', color: result.error || result.failed ? '#92400e' : '#166534', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 12 }}>
          {result.error ? result.error : result.info ? result.info : <>{result.label}: {result.done} done{result.failed ? `, ${result.failed} not done:` : '.'}</>}
          {result.notes?.map((n, i) => <div key={i} style={{ marginTop: 4 }}>⚠ {n}</div>)}
          {result.results?.filter(r => !r.ok).map(r => <div key={r.id} style={{ marginTop: 4 }}><strong>{r.order_ref}</strong> — {r.error}</div>)}
        </div>
      )}

      {chosen.length > 0 && (
        <div style={{ position: 'sticky', top: 0, zIndex: 5, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '8px 12px', marginBottom: 10 }}>
          <strong style={{ fontSize: 13 }}>{chosen.length} selected · {chosen.reduce((a, o) => a + (o.units || 0), 0)} units</strong>
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {chosen.every(o => o.status !== 'CANCELLED') && <>
              <Btn size="sm" variant="ghost" disabled={busy} onClick={() => makeFile('pick')}>🖨 Picking list</Btn>
              {chosen.some(o => o.has_packing_slip) && <Btn size="sm" variant="ghost" disabled={busy} onClick={() => makeFile('slips')}>📄 Packing slips</Btn>}
              <Btn size="sm" variant="ghost" disabled={busy} onClick={() => makeFile('dpd')}>⬇ DPD file</Btn>
            </>}
            {canConfirm && <Btn size="sm" variant="ghost" disabled={busy} onClick={() => run('confirm', 'Confirm')}>Confirm (reserve stock)</Btn>}
            {canConfirm && <Btn size="sm" disabled={busy} onClick={() => run('confirm_and_despatch', 'Confirm and despatch')}>Confirm &amp; mark despatched</Btn>}
            {canDespatch && <Btn size="sm" disabled={busy} onClick={() => run('despatch', 'Mark despatched')}>Mark despatched</Btn>}
            {canCancel && <Btn size="sm" variant="danger" disabled={busy} onClick={() => run('cancel', 'Cancel')}>Cancel</Btn>}
          </span>
        </div>
      )}

      {loading && !data ? <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading...</p> : orders.length === 0 ? (
        <EmptyState title="No orders here" sub={filter === 'todo' ? 'Orders created from the Order Files tab appear here as drafts.' : undefined} />
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--surface-2)' }}>
                <th style={{ ...td, width: 30 }}><input type="checkbox" checked={allPicked} onChange={toggleAll} title="Select all" /></th>
                {['Ref', 'Client', 'Recipient', 'Units', 'Fee', 'Duty', filter === 'despatched' ? 'Despatched' : 'Created', 'Status'].map(h => (
                  <th key={h} style={{ ...td, textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {orders.map(o => (
                <tr key={o.id} style={{ borderTop: '1px solid var(--border)', cursor: 'pointer', background: picked.has(o.id) ? 'var(--surface-2)' : 'transparent' }} onClick={() => setSelectedId(o.id)}>
                  <td style={td} onClick={e => e.stopPropagation()}><input type="checkbox" checked={picked.has(o.id)} onChange={() => toggle(o.id)} /></td>
                  <td style={{ ...td, fontWeight: 700, fontFamily: 'monospace' }}>{o.order_ref}</td>
                  <td style={td}>{o.client_name || clientsById[o.client_id] || '—'}</td>
                  <td style={td}>{o.recipient_name}<div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{o.recipient_postcode}</div></td>
                  <td style={td}>{o.units}</td>
                  <td style={td}>{Number(o.charge_handling) ? fmtCurrency(o.charge_handling) : '—'}</td>
                  <td style={td}>{Number(o.charge_duty) ? fmtCurrency(o.charge_duty) : '—'}</td>
                  <td style={{ ...td, color: 'var(--text-muted)' }}>{fmt(filter === 'despatched' ? o.dispatched_at : o.created_at)}</td>
                  <td style={td}>
                    <StatusBadge status={o.status} />
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                      {[o.has_packing_slip && '📄 slip', o.picking_list_printed_at && 'pick list ✓', o.dpd_exported_at && 'DPD ✓', o.fulfilment_invoice_id && 'billed'].filter(Boolean).join(' · ')}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// VIEW 2 — CSV Import
// ═════════════════════════════════════════════════════════════════════════════

function getToken() { return localStorage.getItem('pld_token') || ''; }

async function uploadCsv(clientId, file) {
  const form = new FormData();
  form.append('client_id', clientId);
  form.append('file', file);
  const res = await fetch('/api/orders/csv-import', {
    method: 'POST',
    headers: { Authorization: `Bearer ${getToken()}` },
    body: form,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
}

function CsvImportRows({ importId, onProgress }) {
  const [statusFilter, setStatusFilter] = useState('all');
  const qs = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
  const { data, loading, refetch } = useApi(`/orders/csv-import/${importId}/rows${qs}`, [importId, statusFilter]);
  const rows = data?.data ?? data ?? [];
  const [acting, setActing] = useState(null);
  const [err, setErr] = useState('');

  async function approve(rowId) {
    setActing(rowId); setErr('');
    try { await apiFetch(`/orders/csv-import/${importId}/rows/${rowId}/approve`, { method: 'POST', body: {} }); refetch(); onProgress?.(); }
    catch (e) { setErr(e.message); } finally { setActing(null); }
  }
  async function reject(rowId) {
    setActing(rowId); setErr('');
    try { await apiFetch(`/orders/csv-import/${importId}/rows/${rowId}/reject`, { method: 'POST', body: {} }); refetch(); onProgress?.(); }
    catch (e) { setErr(e.message); } finally { setActing(null); }
  }

  const ROW_STATUSES = ['PENDING', 'VALID', 'FLAGGED', 'APPROVED', 'REJECTED', 'PROCESSED', 'FAILED'];

  return (
    <div>
      <ErrBanner msg={err} />
      <div style={{ display: 'flex', gap: 4, marginBottom: 14, flexWrap: 'wrap' }}>
        {['all', ...ROW_STATUSES].map(s => (
          <button key={s} onClick={() => setStatusFilter(s)} style={{ padding: '4px 10px', borderRadius: 99, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: 'none', background: statusFilter === s ? 'var(--pld-green)' : 'var(--surface-2)', color: statusFilter === s ? '#fff' : 'var(--text-muted)' }}>{s}</button>
        ))}
      </div>
      {loading ? <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading rows...</p> : rows.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No rows at this status.</p>
      ) : (
        <TableShell headers={['#', 'Recipient', 'SKU', 'Qty', 'Status', 'Flags', '']}>
          {rows.map(r => (
            <tr key={r.id} style={{ borderTop: '1px solid var(--border)' }}>
              <td style={{ padding: '9px 14px', color: 'var(--text-muted)' }}>{r.row_number}</td>
              <td style={{ padding: '9px 14px' }}>{r.parsed_data?.recipient_name || r.raw_data?.recipient_name || '—'}</td>
              <td style={{ padding: '9px 14px', fontFamily: 'monospace', fontSize: 12 }}>{r.parsed_data?.sku || r.raw_data?.sku || '—'}</td>
              <td style={{ padding: '9px 14px' }}>{r.parsed_data?.quantity_units ?? r.raw_data?.quantity ?? '—'}</td>
              <td style={{ padding: '9px 14px' }}><StatusBadge status={r.status} /></td>
              <td style={{ padding: '9px 14px', fontSize: 11, color: '#991b1b' }}>{(r.flag_reasons || []).join(', ')}</td>
              <td style={{ padding: '9px 14px', textAlign: 'right' }}>
                {r.status === 'FLAGGED' && (
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                    <Btn size="sm" variant="ghost" onClick={() => reject(r.id)} disabled={acting === r.id}>Reject</Btn>
                    <Btn size="sm" onClick={() => approve(r.id)} disabled={acting === r.id}>Approve</Btn>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </TableShell>
      )}
    </div>
  );
}

function CsvImportDetail({ importId, onBack, onRefresh }) {
  const { data, loading, refetch } = useApi(`/orders/csv-import/${importId}`, [importId]);
  const imp = data?.data ?? data ?? null;
  const [acting, setActing] = useState(false);
  const [err, setErr] = useState('');

  async function confirmImport() {
    if (!(await uiConfirm('Confirm this import? All valid/approved rows will become fulfilment orders.'))) return;
    setActing(true); setErr('');
    try { await apiFetch(`/orders/csv-import/${importId}/confirm`, { method: 'POST', body: {} }); refetch(); onRefresh(); }
    catch (e) { setErr(e.message); } finally { setActing(false); }
  }

  if (loading || !imp) return <div style={{ padding: 40, color: 'var(--text-muted)' }}>Loading...</div>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 20 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 20, lineHeight: 1, padding: 0, marginTop: 4 }}>&#8592;</button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, fontFamily: 'monospace' }}>{imp.import_ref}</h2>
            <StatusBadge status={imp.status} />
          </div>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
            {imp.filename} · {imp.row_count} rows ({imp.rows_valid} valid, {imp.rows_flagged} flagged) · Uploaded {fmt(imp.created_at)}
          </p>
        </div>
        {imp.status === 'REVIEWING' && <Btn size="sm" onClick={confirmImport} disabled={acting}>Confirm Import</Btn>}
      </div>
      <ErrBanner msg={err} />
      {imp.parse_error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 16 }}>{imp.parse_error}</div>}
      <CsvImportRows importId={importId} onProgress={refetch} />
    </div>
  );
}

function UploadCsvModal({ clients, onClose, onUploaded }) {
  const [clientId, setClientId] = useState('');
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  async function submit(e) {
    e.preventDefault();
    if (!clientId) { setErr('Select a client.'); return; }
    if (!file) { setErr('Choose a CSV file.'); return; }
    setSaving(true); setErr('');
    try {
      const res = await uploadCsv(clientId, file);
      onUploaded(res.data ?? res);
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  }

  return (
    <Modal title="Upload Fulfilment CSV" onClose={onClose} width={480}>
      <form onSubmit={submit}>
        <ErrBanner msg={err} />
        <Field label="Contract client">
          <Select value={clientId} onChange={e => setClientId(e.target.value)} required>
            <option value="">Select client...</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
        </Field>
        <Field label="CSV file">
          <input type="file" accept=".csv" onChange={e => setFile(e.target.files?.[0] || null)} required />
        </Field>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: -6, marginBottom: 14 }}>
          Required columns: recipient_name, address_line1, city, postcode, sku, quantity. Rows are grouped into one order per recipient.
        </p>
        <BtnRow>
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Uploading...' : 'Upload & Parse'}</Btn>
        </BtnRow>
      </form>
    </Modal>
  );
}

const CSV_IMPORT_STATUSES = ['UPLOADING', 'PARSING', 'REVIEWING', 'CONFIRMED', 'PROCESSING', 'COMPLETE', 'FAILED', 'CANCELLED'];

function CsvImportView() {
  const [selectedId, setSelectedId] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const { data: clientData } = useApi('/clients');
  const clients = clientData?.clients ?? clientData ?? [];

  const qs = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
  const { data, loading, refetch } = useApi(`/orders/csv-import${qs}`, [statusFilter]);
  const imports = data?.data ?? data ?? [];

  if (selectedId) return (
    <CsvImportDetail importId={selectedId} onBack={() => { setSelectedId(null); refetch(); }} onRefresh={refetch} />
  );

  return (
    <div>
      {showUpload && (
        <UploadCsvModal
          clients={clients}
          onClose={() => setShowUpload(false)}
          onUploaded={(imp) => {
            setShowUpload(false);
            refetch();
            setSelectedId(imp.id);
          }}
        />
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {['all', ...CSV_IMPORT_STATUSES].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)} style={{ padding: '5px 12px', borderRadius: 99, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none', background: statusFilter === s ? 'var(--pld-green)' : 'var(--surface-2)', color: statusFilter === s ? '#fff' : 'var(--text-muted)' }}>{s}</button>
          ))}
        </div>
        <Btn onClick={() => setShowUpload(true)}>+ Upload CSV</Btn>
      </div>

      {loading ? <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading...</p> : imports.length === 0 ? (
        <EmptyState title="No CSV imports found" sub="Upload a fulfilment CSV above to get started." />
      ) : (
        <TableShell headers={['Ref', 'Client', 'Filename', 'Rows', 'Uploaded', 'Status']}>
          {imports.map(imp => (
            <ClickRow key={imp.id} onClick={() => setSelectedId(imp.id)}>
              <td style={{ padding: '11px 14px', fontWeight: 700, fontFamily: 'monospace' }}>{imp.import_ref}</td>
              <td style={{ padding: '11px 14px' }}>{imp.client_name || '—'}</td>
              <td style={{ padding: '11px 14px', color: 'var(--text-muted)' }}>{imp.filename}</td>
              <td style={{ padding: '11px 14px', color: 'var(--text-muted)' }}>{imp.row_count}</td>
              <td style={{ padding: '11px 14px', color: 'var(--text-muted)' }}>{fmt(imp.created_at)}</td>
              <td style={{ padding: '11px 14px' }}><StatusBadge status={imp.status} /></td>
            </ClickRow>
          ))}
        </TableShell>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// VIEW 3 — Email Queue
// ═════════════════════════════════════════════════════════════════════════════

const QUEUE_STATUSES = ['PENDING', 'PARSED', 'NEEDS_REVIEW', 'CONFIRMED', 'REJECTED', 'FAILED'];

// Generic line-item editor shared by both order types below. `mode` picks
// which extra numeric field to show: 'unit_price' for sales orders,
// 'lpa' for fulfilment orders (LPA drives duty calc at confirm time).
function EmailOrderLinesEditor({ lines, setLines, stockItems, mode }) {
  function addLine() { setLines(ls => [...ls, { stock_item_id: '', quantity_units: '', unit_price: '', lpa: '' }]); }
  function removeLine(i) { setLines(ls => ls.filter((_, idx) => idx !== i)); }
  function setLine(i, k, v) { setLines(ls => ls.map((l, idx) => idx === i ? { ...l, [k]: v } : l)); }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <label style={{ fontSize: 13, fontWeight: 600 }}>Order lines *</label>
        <Btn size="sm" variant="ghost" type="button" onClick={addLine}>+ Add line</Btn>
      </div>
      {lines.length === 0 && <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>No lines added yet.</p>}
      {lines.map((l, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '3fr 1fr 1fr auto', gap: 8, marginBottom: 8, alignItems: 'center' }}>
          <Select value={l.stock_item_id} onChange={e => setLine(i, 'stock_item_id', e.target.value)} required>
            <option value="">Product...</option>
            {stockItems.map(s => <option key={s.id} value={s.id}>{s.name} ({s.sku})</option>)}
          </Select>
          <Input type="number" min="1" step="1" value={l.quantity_units} onChange={e => setLine(i, 'quantity_units', e.target.value)} placeholder="Qty (units)" required />
          {mode === 'unit_price' ? (
            <Input type="number" min="0" step="0.01" value={l.unit_price} onChange={e => setLine(i, 'unit_price', e.target.value)} placeholder="Unit £" required />
          ) : (
            <Input type="number" min="0" step="0.01" value={l.lpa} onChange={e => setLine(i, 'lpa', e.target.value)} placeholder="LPA (optional)" />
          )}
          <button type="button" onClick={() => removeLine(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 18, lineHeight: 1, padding: '0 4px' }}>&#215;</button>
        </div>
      ))}
    </div>
  );
}

function linesFromExtracted(extracted) {
  return (extracted.lines || []).map(l => ({
    stock_item_id: l.matched_stock_item_id || '',
    quantity_units: l.quantity_units ?? l.quantity_cases ?? '',
    unit_price: '',
    lpa: '',
  }));
}

// Best-effort split of the AI's single delivery_address string into line1 —
// there's no structured address in extracted_data (it's free text from the
// email), so this just seeds line1 and leaves the rest for a human to tidy.
function addressFromExtracted(extracted) {
  return { line1: extracted.delivery_address || '', line2: '', city: '', postcode: '', country: 'United Kingdom' };
}

function ResolveQueueItemModal({ item, onClose, onResolved, onReread }) {
  const extracted = item.extracted_data || {};
  const [resolvedAs, setResolvedAs] = useState(
    extracted.order_type === 'FULFILMENT' || item.matched_client_name ? 'FULFILMENT_ORDER' : 'SALES_ORDER'
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [rereading, setRereading] = useState(false);
  async function reread() {
    setRereading(true); setErr('');
    try { await apiFetch(`/orders/email-queue/${item.id}/reread`, { method: 'POST', body: {} }); await onReread(); }
    catch (e) { setErr(e.message); setRereading(false); }
  }

  const { data: stockData } = useApi('/stock');
  const allStock = stockData?.stock_items ?? stockData ?? [];
  const { data: outletData } = useApi('/own-brand/outlets?limit=all');
  const outlets = outletData?.outlets ?? outletData ?? [];
  const { data: clientData } = useApi('/clients');
  const clients = clientData?.clients ?? clientData ?? [];

  // Sales Order form state
  const [soForm, setSoForm] = useState({
    customer_type: 'outlet',
    outlet_id: item.matched_outlet_id || '',
    delivery_method: 'DPD',
    notes: extracted.special_instructions || '',
    delivery_address: addressFromExtracted(extracted),
  });
  const [soLines, setSoLines] = useState(linesFromExtracted(extracted));
  const soStockItems = allStock.filter(s => s.category === 'bottle_finished' && s.owner_type === 'PLD' && s.is_active !== false);
  // The outlet's price list and discounts fill the prices, as on a new sales order.
  const [soPrices, setSoPrices] = useState({ name: null, map: {}, discounts: {} });
  useEffect(() => {
    let live = true;
    if (soForm.customer_type !== 'outlet' || !soForm.outlet_id) {
      const none = { name: null, map: {}, discounts: {} };
      setSoPrices(none); setSoLines(ls => reprice(ls, none)); return undefined;
    }
    apiFetch(`/price-lists/for-outlet?outlet_id=${soForm.outlet_id}`).then(r => {
      if (!live) return;
      const p = { name: r?.name || null, map: r?.prices || {}, discounts: r?.discounts || {}, gross: !!r?.use_gross_price };
      setSoPrices(p); setSoLines(ls => reprice(ls, p));
    }).catch(() => {});
    return () => { live = false; };
  }, [soForm.outlet_id, soForm.customer_type]);

  // Fulfilment Order form state
  const [foForm, setFoForm] = useState({
    client_id: item.matched_client_id || '',
    delivery_method: 'DPD',
    notes: extracted.special_instructions || '',
    recipient: {
      name: extracted.sender_name || item.from_name || '',
      address_line1: extracted.delivery_address || '',
      address_line2: '', city: '', postcode: '', country: 'United Kingdom',
      email: item.from_email || '', phone: '',
    },
  });
  const [foLines, setFoLines] = useState(linesFromExtracted(extracted));
  const foStockItems = allStock.filter(s => s.owner_type === 'CONTRACT' && s.owner_client_id === foForm.client_id && s.is_active !== false);

  const setSo = k => e => setSoForm(f => ({ ...f, [k]: e.target.value }));
  const setSoAddr = k => e => setSoForm(f => ({ ...f, delivery_address: { ...f.delivery_address, [k]: e.target.value } }));
  const setFo = k => e => setFoForm(f => ({ ...f, [k]: e.target.value }));
  const setFoRecip = k => e => setFoForm(f => ({ ...f, recipient: { ...f.recipient, [k]: e.target.value } }));

  async function submit(e) {
    e.preventDefault();
    setSaving(true); setErr('');
    try {
      if (resolvedAs === 'SPAM' || resolvedAs === 'OTHER') {
        await apiFetch(`/orders/email-queue/${item.id}/reject`, { method: 'POST', body: { resolved_as: resolvedAs } });
      } else if (resolvedAs === 'SALES_ORDER') {
        if (soLines.length === 0 || !soLines[0].stock_item_id) { setErr('Add at least one order line.'); setSaving(false); return; }
        if (soForm.customer_type === 'outlet' && !soForm.outlet_id) { setErr('Select an outlet.'); setSaving(false); return; }
        const order_data = {
          source: 'EMAIL',
          customer_type: soForm.customer_type,
          outlet_id: soForm.outlet_id || undefined,
          delivery_method: soForm.delivery_method,
          notes: soForm.notes,
          delivery_address: soForm.delivery_method !== 'CLIENT_COLLECT' ? soForm.delivery_address : undefined,
          lines: soLines.map(l => ({ stock_item_id: l.stock_item_id, quantity_units: parseInt(l.quantity_units), unit_price: parseFloat(l.unit_price) })),
        };
        await apiFetch(`/orders/email-queue/${item.id}/resolve`, { method: 'POST', body: { resolved_as: resolvedAs, order_data } });
      } else if (resolvedAs === 'FULFILMENT_ORDER') {
        if (!foForm.client_id) { setErr('Select a client.'); setSaving(false); return; }
        if (foLines.length === 0 || !foLines[0].stock_item_id) { setErr('Add at least one order line.'); setSaving(false); return; }
        if (!foForm.recipient.name || !foForm.recipient.address_line1 || !foForm.recipient.city || !foForm.recipient.postcode) {
          setErr('Recipient name, address line 1, city and postcode are required.'); setSaving(false); return;
        }
        const order_data = {
          source: 'EMAIL',
          client_id: foForm.client_id,
          recipient: foForm.recipient,
          delivery_method: foForm.delivery_method,
          notes: foForm.notes,
          lines: foLines.map(l => ({
            stock_item_id: l.stock_item_id,
            quantity_units: parseInt(l.quantity_units),
            lpa: l.lpa ? parseFloat(l.lpa) : undefined,
          })),
        };
        await apiFetch(`/orders/email-queue/${item.id}/resolve`, { method: 'POST', body: { resolved_as: resolvedAs, order_data } });
      }
      onResolved();
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  }

  return (
    <Modal title={`Resolve email — ${item.from_email}`} onClose={onClose} width={720}>
      <ErrBanner msg={err} />
      <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 14, marginBottom: 16, fontSize: 13 }}>
        <div style={{ marginBottom: 6 }}><strong>Subject:</strong> {item.subject_line || '(no subject)'}</div>
        <div style={{ marginBottom: 6 }}><strong>Match confidence:</strong> {pct(item.match_confidence)}</div>
        <div style={{ marginBottom: 6 }}><strong>Matched:</strong> {item.matched_outlet_name || item.matched_client_name || 'None'}</div>
        {extracted.match_notes && <div style={{ marginBottom: 6 }}><strong>AI notes:</strong> {extracted.match_notes}</div>}
        {extracted.flags?.length > 0 && <div style={{ marginTop: 6, color: '#991b1b' }}><strong>Flags:</strong> {extracted.flags.join(', ')}</div>}
        {onReread && (
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Btn size="sm" variant="ghost" type="button" disabled={rereading} onClick={reread}>{rereading ? 'Reading…' : 'Read again (with attachments)'}</Btn>
            <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Blake reads the email and any attached PO, photo or spreadsheet again.</span>
          </div>
        )}
        <p style={{ margin: '8px 0 0', color: 'var(--text-muted)' }}>Fields below are pre-filled from the email where possible — check them over before confirming.</p>
      </div>

      <form onSubmit={submit}>
        <Field label="Resolve as">
          <Select value={resolvedAs} onChange={e => setResolvedAs(e.target.value)}>
            <option value="SALES_ORDER">Sales Order (own brand)</option>
            <option value="FULFILMENT_ORDER">Fulfilment Order (client)</option>
            <option value="SPAM">Spam</option>
            <option value="OTHER">Other (dismiss)</option>
          </Select>
        </Field>

        {resolvedAs === 'SALES_ORDER' && (
          <>
            <Grid2>
              <Field label="Customer type">
                <Select value={soForm.customer_type} onChange={setSo('customer_type')}>
                  <option value="outlet">Outlet (trade)</option>
                  <option value="consumer">Consumer</option>
                  <option value="client">Contract client</option>
                </Select>
              </Field>
              <Field label="Delivery method">
                <Select value={soForm.delivery_method} onChange={setSo('delivery_method')}>
                  {['DPD', 'PALLET_HAULAGE', 'CLIENT_COLLECT', 'IN_HOUSE'].map(m => <option key={m} value={m}>{m.replace(/_/g, ' ')}</option>)}
                </Select>
              </Field>
            </Grid2>
            {soForm.customer_type === 'outlet' && (
              <Field label="Outlet">
                <Select value={soForm.outlet_id} onChange={setSo('outlet_id')} required>
                  <option value="">Select outlet...</option>
                  {outlets.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                </Select>
                {soForm.outlet_id && <span style={{ fontSize: 12, color: soPrices.name ? 'var(--text-muted)' : '#b45309' }}>{soPrices.name ? `Prices from the ${soPrices.name} price list${soPrices.gross ? ' (include VAT)' : ''}` : 'No price list on this outlet — type prices in, or set one on the outlet'}</span>}
              </Field>
            )}
            {soForm.delivery_method !== 'CLIENT_COLLECT' && (
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 8 }}>Delivery address</label>
                <Grid2>
                  <Input placeholder="Line 1" value={soForm.delivery_address.line1} onChange={setSoAddr('line1')} />
                  <Input placeholder="Line 2" value={soForm.delivery_address.line2} onChange={setSoAddr('line2')} />
                </Grid2>
                <div style={{ height: 8 }} />
                <Grid2>
                  <Input placeholder="City" value={soForm.delivery_address.city} onChange={setSoAddr('city')} />
                  <Input placeholder="Postcode" value={soForm.delivery_address.postcode} onChange={setSoAddr('postcode')} />
                </Grid2>
              </div>
            )}
            <div style={{ marginBottom: 14 }}>
              <OrderLinesEditor lines={soLines} setLines={setSoLines} stockItems={soStockItems} prices={soPrices} />
            </div>
            <Field label="Notes"><Textarea rows={2} value={soForm.notes} onChange={setSo('notes')} /></Field>
          </>
        )}

        {resolvedAs === 'FULFILMENT_ORDER' && (
          <>
            <Grid2>
              <Field label="Contract client">
                <Select value={foForm.client_id} onChange={setFo('client_id')} required>
                  <option value="">Select client...</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </Select>
              </Field>
              <Field label="Delivery method">
                <Select value={foForm.delivery_method} onChange={setFo('delivery_method')}>
                  {['DPD', 'PALLET_HAULAGE', 'CLIENT_COLLECT', 'IN_HOUSE'].map(m => <option key={m} value={m}>{m.replace(/_/g, ' ')}</option>)}
                </Select>
              </Field>
            </Grid2>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 8 }}>Recipient</label>
              <Grid2>
                <Input placeholder="Name" value={foForm.recipient.name} onChange={setFoRecip('name')} />
                <Input placeholder="Email" value={foForm.recipient.email} onChange={setFoRecip('email')} />
              </Grid2>
              <div style={{ height: 8 }} />
              <Grid2>
                <Input placeholder="Address line 1" value={foForm.recipient.address_line1} onChange={setFoRecip('address_line1')} />
                <Input placeholder="Address line 2" value={foForm.recipient.address_line2} onChange={setFoRecip('address_line2')} />
              </Grid2>
              <div style={{ height: 8 }} />
              <Grid2>
                <Input placeholder="City" value={foForm.recipient.city} onChange={setFoRecip('city')} />
                <Input placeholder="Postcode" value={foForm.recipient.postcode} onChange={setFoRecip('postcode')} />
              </Grid2>
              <div style={{ height: 8 }} />
              <Input placeholder="Phone" value={foForm.recipient.phone} onChange={setFoRecip('phone')} />
            </div>
            {!foForm.client_id ? (
              <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 14 }}>Select a client above to choose products.</p>
            ) : (
              <div style={{ marginBottom: 14 }}>
                <EmailOrderLinesEditor lines={foLines} setLines={setFoLines} stockItems={foStockItems} mode="lpa" />
              </div>
            )}
            <Field label="Notes"><Textarea rows={2} value={foForm.notes} onChange={setFo('notes')} /></Field>
          </>
        )}

        <BtnRow>
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Saving...' : 'Confirm'}</Btn>
        </BtnRow>
      </form>
    </Modal>
  );
}


// ── New fulfilment order, typed in by hand (Joe, 22 Oct 2026) ──────────────────
// For orders that come by phone, a message or anything Blake can't read. It
// goes in as a draft, like orders from files and emails, to confirm from Orders.
export function CreateFulfilmentOrderModal({ onClose, onSaved }) {
  const { data: clientData } = useApi('/clients');
  const clients = (clientData?.clients ?? clientData ?? []).filter(c => c.is_active !== false);
  const { data: stockData } = useApi('/stock');
  const allStock = stockData?.stock_items ?? stockData ?? [];
  const [form, setForm] = useState({
    client_id: '', external_ref: '', delivery_method: 'DPD', notes: '',
    recipient: { name: '', company: '', address_line1: '', address_line2: '', city: '', postcode: '', country: 'United Kingdom', email: '', phone: '' },
  });
  const [lines, setLines] = useState([{ stock_item_id: '', quantity_units: '', unit_price: '', lpa: '' }]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const stockItems = allStock.filter(s => s.owner_type === 'CONTRACT' && s.owner_client_id === form.client_id && s.is_active !== false);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const setR = k => e => setForm(f => ({ ...f, recipient: { ...f.recipient, [k]: e.target.value } }));
  const collect = form.delivery_method === 'CLIENT_COLLECT';

  async function submit(e) {
    e.preventDefault(); setErr('');
    const r = form.recipient;
    if (!form.client_id) { setErr('Choose the client.'); return; }
    const ls = lines.filter(l => l.stock_item_id && Number(l.quantity_units) > 0);
    if (!ls.length) { setErr('Add at least one product and quantity.'); return; }
    if (!r.name.trim()) { setErr('Enter who it’s going to.'); return; }
    if (!collect && (!r.address_line1.trim() || !r.city.trim() || !r.postcode.trim())) { setErr('Enter the address: line 1, town and postcode.'); return; }
    setSaving(true);
    try {
      const recipient = collect
        ? { ...r, address_line1: r.address_line1 || 'Collecting from PLD', city: r.city || 'Aldington', postcode: r.postcode || 'TN25 7DF' }
        : r;
      const res = await apiFetch('/orders/fulfilment', { method: 'POST', body: {
        source: 'IN_HOUSE', client_id: form.client_id, external_ref: form.external_ref || undefined,
        delivery_method: form.delivery_method, notes: form.notes || undefined, recipient,
        lines: ls.map(l => ({ stock_item_id: l.stock_item_id, quantity_units: parseInt(l.quantity_units, 10), lpa: l.lpa ? parseFloat(l.lpa) : undefined })),
      } });
      onSaved(res?.data ?? res);
    } catch (e2) { setErr(e2.message); } finally { setSaving(false); }
  }

  return (
    <Modal title="New fulfilment order" onClose={onClose} width={720}>
      <form onSubmit={submit}>
        <ErrBanner msg={err} />
        <Grid2>
          <Field label="Client *">
            <Select value={form.client_id} onChange={e => { set('client_id')(e); setLines([{ stock_item_id: '', quantity_units: '', unit_price: '', lpa: '' }]); }}>
              <option value="">Choose the client…</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </Field>
          <Field label="Their order number"><Input value={form.external_ref} onChange={set('external_ref')} placeholder="e.g. #3740 (optional)" /></Field>
        </Grid2>
        <Field label="Delivery">
          <Select value={form.delivery_method} onChange={set('delivery_method')}>
            <option value="DPD">DPD parcel</option>
            <option value="PALLET_HAULAGE">Pallet / haulier</option>
            <option value="CLIENT_COLLECT">Customer collects</option>
            <option value="IN_HOUSE">We deliver</option>
          </Select>
        </Field>
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 8 }}>{collect ? 'Who’s collecting' : 'Deliver to'}</label>
          <Grid2>
            <Input placeholder="Name *" value={form.recipient.name} onChange={setR('name')} />
            <Input placeholder="Company" value={form.recipient.company} onChange={setR('company')} />
          </Grid2>
          {!collect && (<>
            <div style={{ height: 8 }} />
            <Grid2>
              <Input placeholder="Address line 1 *" value={form.recipient.address_line1} onChange={setR('address_line1')} />
              <Input placeholder="Address line 2" value={form.recipient.address_line2} onChange={setR('address_line2')} />
            </Grid2>
            <div style={{ height: 8 }} />
            <Grid2>
              <Input placeholder="Town *" value={form.recipient.city} onChange={setR('city')} />
              <Input placeholder="Postcode *" value={form.recipient.postcode} onChange={setR('postcode')} />
            </Grid2>
          </>)}
          <div style={{ height: 8 }} />
          <Grid2>
            <Input placeholder="Email (for DPD tracking)" value={form.recipient.email} onChange={setR('email')} />
            <Input placeholder="Phone" value={form.recipient.phone} onChange={setR('phone')} />
          </Grid2>
        </div>
        {!form.client_id
          ? <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 14 }}>Choose the client to pick from their products.</p>
          : stockItems.length === 0
            ? <p style={{ fontSize: 13, color: '#b45309', marginBottom: 14 }}>This client has no products in Blake yet — their stock items need to be set up (Stock → item → Owned by).</p>
            : <div style={{ marginBottom: 14 }}><EmailOrderLinesEditor lines={lines} setLines={setLines} stockItems={stockItems} mode="lpa" /></div>}
        <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={set('notes')} placeholder="Anything for the packer — gift note, delivery instructions…" /></Field>
        <BtnRow>
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Saving…' : 'Create draft order'}</Btn>
        </BtnRow>
      </form>
    </Modal>
  );
}

const QUEUE_FILTERS = [
  { key: 'open', label: 'To deal with', status: 'PENDING,PARSED,NEEDS_REVIEW,FAILED' },
  { key: 'done', label: 'Done', status: 'CONFIRMED' },
  { key: 'dismissed', label: 'Not orders', status: 'REJECTED' },
  { key: 'all', label: 'All', status: '' },
];
const RULE_LABEL = { fulfilment: 'Fulfilment', sales: 'Sales', ignore: 'Ignored' };

function SenderMenu({ it, clients, onDone, onError }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(null); // 'fulfilment' while picking a client
  const [clientId, setClientId] = useState('');
  const [scope, setScope] = useState('email');
  const domain = (it.from_email || '').includes('@') ? `@${it.from_email.split('@').pop()}` : '';
  async function save(action) {
    try {
      const r = await apiFetch(`/orders/email-queue/${it.id}/sender`, { method: 'POST', body: { scope, action, client_id: action === 'fulfilment' ? clientId : undefined } });
      setOpen(false); setMode(null);
      onDone(r.data);
    } catch (e) { onError(e.message); }
  }
  if (/@pleasantlanddistillery\.com$/i.test(it.from_email || '')) {
    return <span title="This shows our own address, so the real sender isn't known — Blake re-checks these when it next checks the inbox. Open the email to see who it's from." style={{ fontSize: 11.5, color: 'var(--text-muted)', cursor: 'help' }}>Our address</span>;
  }
  if (!open) return <Btn size="sm" variant="ghost" onClick={() => setOpen(true)} title="Tell Blake what this sender's emails are">Sender…</Btn>;
  return (
    <div style={{ position: 'absolute', right: 8, top: 34, zIndex: 20, width: 290, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', boxShadow: 'var(--shadow-lg)', padding: 12, textAlign: 'left', whiteSpace: 'normal' }}>
      <div style={{ fontSize: 12, marginBottom: 8 }}>
        Apply to{' '}
        <select value={scope} onChange={e => setScope(e.target.value)} style={{ fontSize: 12 }}>
          <option value="email">{it.from_email}</option>
          {domain && <option value="domain">everyone at {domain}</option>}
        </select>
      </div>
      {mode === 'fulfilment' ? (
        <>
          <div style={{ fontSize: 12, marginBottom: 6 }}>Sends fulfilment orders for:</div>
          <Select value={clientId} onChange={e => setClientId(e.target.value)}>
            <option value="">— choose client —</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
          <div style={{ display: 'flex', gap: 6, marginTop: 8, justifyContent: 'flex-end' }}>
            <Btn size="sm" variant="ghost" onClick={() => setMode(null)}>Back</Btn>
            <Btn size="sm" disabled={!clientId} onClick={() => save('fulfilment')}>Save</Btn>
          </div>
        </>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Btn size="sm" variant="ghost" onClick={() => setMode('fulfilment')}>📦 Fulfilment client — import their order files</Btn>
          <Btn size="sm" variant="ghost" onClick={() => save('sales')}>🧾 Trade customer — sales orders</Btn>
          <Btn size="sm" variant="ghost" onClick={() => save('ignore')} style={{ color: '#991b1b' }}>🚫 Never orders — ignore from now on</Btn>
          <Btn size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn>
        </div>
      )}
    </div>
  );
}

function SendersPanel({ clients, onChanged }) {
  const { data, refetch } = useApi('/orders/email-senders', []);
  const rules = data?.data ?? data ?? [];
  const [form, setForm] = useState({ pattern: '', action: 'fulfilment', client_id: '' });
  const [err, setErr] = useState('');
  async function add(e) {
    e.preventDefault(); setErr('');
    try {
      await apiFetch('/orders/email-senders', { method: 'POST', body: form });
      setForm({ pattern: '', action: form.action, client_id: '' }); refetch(); onChanged?.();
    } catch (e2) { setErr(e2.message); }
  }
  async function remove(r) {
    if (!(await uiConfirm(`Remove the rule for ${r.pattern}?`))) return;
    try { await apiFetch(`/orders/email-senders/${r.id}`, { method: 'DELETE' }); refetch(); } catch (e) { setErr(e.message); }
  }
  return (
    <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 16, marginBottom: 16 }}>
      <p style={{ margin: '0 0 10px', fontSize: 13 }}>
        <strong>Fulfilment</strong> senders' attached order files go to Order Files for that client. <strong>Ignored</strong> senders are dismissed automatically.
        Everyone else is treated as a trade customer ordering from us (sales orders).
      </p>
      <ErrBanner msg={err} />
      <form onSubmit={add} style={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr 1.6fr auto', gap: 8, alignItems: 'center', marginBottom: 12 }}>
        <Input value={form.pattern} onChange={e => setForm(f => ({ ...f, pattern: e.target.value }))} placeholder="name@shop.com or @shop.com" />
        <Select value={form.action} onChange={e => setForm(f => ({ ...f, action: e.target.value }))}>
          <option value="fulfilment">Fulfilment</option>
          <option value="sales">Sales</option>
          <option value="ignore">Ignore</option>
        </Select>
        {form.action === 'fulfilment' ? (
          <Select value={form.client_id} onChange={e => setForm(f => ({ ...f, client_id: e.target.value }))}>
            <option value="">— client —</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
        ) : <span />}
        <Btn type="submit" size="sm" disabled={!form.pattern.trim()}>Add</Btn>
      </form>
      {!rules.length ? <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>No senders set up yet.</p> : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <tbody>
            {rules.map(r => (
              <tr key={r.id} style={{ borderTop: '1px solid var(--border)' }}>
                <td style={{ padding: '6px 4px', wordBreak: 'break-all' }}>{r.pattern}</td>
                <td style={{ padding: '6px 4px' }}>{RULE_LABEL[r.action]}{r.client_name ? ` — ${r.client_name}` : ''}</td>
                <td style={{ padding: '6px 4px', textAlign: 'right' }}><Btn size="sm" variant="ghost" onClick={() => remove(r)}>Remove</Btn></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function EmailQueueView({ onOpenImport }) {
  const [filter, setFilter] = useState('open');
  const [selected, setSelected] = useState(null);
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState(null);
  const [checkErr, setCheckErr] = useState('');
  const [notice, setNotice] = useState('');
  const [showSenders, setShowSenders] = useState(false);
  const [picked, setPicked] = useState(() => new Set());
  const f = QUEUE_FILTERS.find(x => x.key === filter);
  const qs = f.status ? `?status=${f.status}` : '';
  const { data, loading, refetch } = useApi(`/orders/email-queue${qs}`, [filter]);
  const items = data?.data ?? data ?? [];
  const { data: clientData } = useApi('/clients');
  const clients = clientData?.clients ?? clientData?.data ?? (Array.isArray(clientData) ? clientData : []);
  useEffect(() => { setPicked(new Set()); }, [filter]);

  const [importing, setImporting] = useState(null);
  async function importAttachments(it) {
    setImporting(it.id); setCheckErr(''); setCheckResult(null);
    try {
      const res = await apiFetch(`/orders/email-queue/${it.id}/import-attachments`, { method: 'POST', body: {} });
      const files = res.data || [];
      if (res.reread?.error) setCheckErr(`Couldn't read the email again: ${res.reread.error}`);
      else if (res.reread) {
        const n = res.reread.extracted?.lines?.length || 0;
        setNotice(n ? `Read ${it.from_email}'s email again with its attachments: ${n} line${n === 1 ? '' : 's'} found — open Review to check them.` : `Read ${it.from_email}'s email again with its attachments, but no order lines were found.`);
      } else if (!files.length) setCheckErr(`No CSV, Excel or PDF attachments on the email from ${it.from_email}.`);
      refetch();
    } catch (e) { setCheckErr(e.message); } finally { setImporting(null); }
  }
  async function dismiss(ids) {
    setCheckErr('');
    try {
      const r = await apiFetch('/orders/email-queue/bulk-reject', { method: 'POST', body: { ids } });
      setNotice(`${r.data?.dismissed ?? ids.length} email(s) marked as not an order.`);
      setPicked(new Set()); refetch();
    } catch (e) { setCheckErr(e.message); }
  }
  async function markDone(ids) {
    setCheckErr('');
    try {
      const r = await apiFetch('/orders/email-queue/bulk-done', { method: 'POST', body: { ids } });
      setNotice(`${r.data?.done ?? ids.length} email(s) moved to Done.`);
      setPicked(new Set()); refetch();
    } catch (e) { setCheckErr(e.message); }
  }
  async function restore(it) {
    setCheckErr('');
    try {
      await apiFetch(`/orders/email-queue/${it.id}/restore`, { method: 'POST', body: {} });
      setNotice(`Restored the email from ${it.from_email}${it.sender_rule?.action === 'ignore' ? ` — its sender (${it.sender_rule.pattern}) is still set to Ignore; remove that under Senders or their next email will be dismissed too` : ''}.`);
      refetch();
    } catch (e) { setCheckErr(e.message); }
  }
  function senderSaved(d) {
    const a = d?.applied || {};
    const what = d?.rule?.action === 'ignore' ? `${a.dismissed || 0} dismissed`
      : d?.rule?.action === 'fulfilment' ? `${a.imported || 0} email(s) imported to Order Files`
      : 'treated as sales from now on';
    setNotice(`Saved: ${d?.rule?.pattern} → ${RULE_LABEL[d?.rule?.action] || ''}. ${what}.${a.errors?.length ? ` Problems: ${a.errors.join('; ')}` : ''}`);
    refetch();
  }

  // The check runs on the server in the background; follow it until done,
  // refreshing the list as new emails land.
  const [checkStage, setCheckStage] = useState('');
  async function followCheck() {
    for (let i = 0; i < 600; i++) {            // up to ~20 minutes
      await new Promise(r => setTimeout(r, 2000));
      let st;
      try { st = (await apiFetch('/orders/email-queue/check-inbox/status')).data; } catch { continue; }
      setCheckStage(st.stage || '');
      if (i % 3 === 0) refetch();
      if (!st.running) {
        if (st.error) setCheckErr(st.error); else setCheckResult(st.result);
        refetch();
        return;
      }
    }
  }
  async function checkInbox() {
    setChecking(true); setCheckErr(''); setCheckResult(null); setNotice(''); setCheckStage('Starting');
    try {
      await apiFetch('/orders/email-queue/check-inbox', { method: 'POST', body: {} });
      await followCheck();
    } catch (e) { setCheckErr(e.message); } finally { setChecking(false); setCheckStage(''); }
  }
  // If a check is already running (e.g. the page was reloaded), pick it up.
  useEffect(() => {
    apiFetch('/orders/email-queue/check-inbox/status').then(r => {
      if (r?.data?.running) { setChecking(true); setCheckStage(r.data.stage || ''); followCheck().finally(() => { setChecking(false); setCheckStage(''); }); }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [rereadCount, setRereadCount] = useState(0);
  // The list leaves out what was read from the email; load it so the form pre-fills.
  async function openReview(it) {
    try { const r = await apiFetch(`/orders/email-queue/${it.id}`); setSelected({ ...it, ...(r?.data ?? r) }); }
    catch { setSelected(it); }
  }
  const allPicked = items.length > 0 && items.every(i => picked.has(i.id));
  const toggle = (id) => setPicked(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const td = { padding: '9px 10px', verticalAlign: 'top' };
  const ellipsis = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

  return (
    <div>
      {selected && (
        <ResolveQueueItemModal
          key={`${selected.id}-${selected.updated_at || ''}-${rereadCount}`}
          item={selected}
          onReread={async () => { setRereadCount(n => n + 1); await openReview(selected); refetch(); }}
          onClose={() => setSelected(null)}
          onResolved={() => { setSelected(null); refetch(); }}
        />
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {QUEUE_FILTERS.map(x => (
            <button key={x.key} onClick={() => setFilter(x.key)} style={{ padding: '5px 12px', borderRadius: 99, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none', background: filter === x.key ? 'var(--pld-green)' : 'var(--surface-2)', color: filter === x.key ? '#fff' : 'var(--text-muted)' }}>{x.label}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn variant="ghost" onClick={() => setShowSenders(s => !s)}>{showSenders ? 'Hide senders' : 'Senders'}</Btn>
          <Btn onClick={checkInbox} disabled={checking}>{checking ? `${checkStage || 'Checking'}…` : 'Check inbox now'}</Btn>
        </div>
      </div>

      {showSenders && <SendersPanel clients={clients} onChanged={refetch} />}

      <ErrBanner msg={checkErr} />
      {checkResult && (
        <div style={{ background: '#dcfce7', color: '#166534', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 12 }}>
          Checked orders@ — {checkResult.queued} new email(s) found, {checkResult.processed} read{checkResult.not_orders ? ` (${checkResult.not_orders} weren't orders — moved to Not orders)` : ''}{checkResult.errors > 0 ? `, ${checkResult.errors} couldn't be read — they'll be tried again next check` : ''}.
        </div>
      )}
      {notice && <div style={{ background: 'var(--surface-2)', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 12 }}>{notice}</div>}

      {picked.size > 0 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '8px 12px', marginBottom: 10 }}>
          <strong style={{ fontSize: 13 }}>{picked.size} selected</strong>
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            {filter !== 'done' && <Btn size="sm" onClick={() => markDone([...picked])}>Done</Btn>}
            <Btn size="sm" variant="danger" onClick={() => dismiss([...picked])}>Not orders</Btn>
          </span>
        </div>
      )}

      {loading && !data ? <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading...</p> : items.length === 0 ? (
        <EmptyState title={filter === 'open' ? 'Nothing to deal with' : 'No emails here'} sub={filter === 'open' ? 'New emails appear here after "Check inbox now".' : undefined} />
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'visible' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: 32 }} /><col style={{ width: 92 }} /><col style={{ width: '24%' }} /><col /><col style={{ width: 150 }} /><col style={{ width: 210 }} />
            </colgroup>
            <thead>
              <tr style={{ background: 'var(--surface-2)' }}>
                <th style={td}><input type="checkbox" checked={allPicked} onChange={() => setPicked(allPicked ? new Set() : new Set(items.map(i => i.id)))} /></th>
                {['Received', 'From', 'Subject', 'Status', ''].map(h => <th key={h} style={{ ...td, textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)' }}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {items.map(it => (
                <tr key={it.id} style={{ borderTop: '1px solid var(--border)', background: picked.has(it.id) ? 'var(--surface-2)' : 'transparent' }}>
                  <td style={td}><input type="checkbox" checked={picked.has(it.id)} onChange={() => toggle(it.id)} /></td>
                  <td style={{ ...td, color: 'var(--text-muted)', fontSize: 12 }}>{fmtDateTime(it.received_at)}</td>
                  <td style={td}>
                    <div style={ellipsis} title={it.from_email}>{it.from_name || it.from_email}</div>
                    {it.from_name && <div style={{ ...ellipsis, fontSize: 11, color: 'var(--text-muted)' }} title={it.from_email}>{it.from_email}</div>}
                    {it.sender_rule && <div style={{ fontSize: 11, color: it.sender_rule.action === 'ignore' ? '#991b1b' : 'var(--pld-green)' }}>{RULE_LABEL[it.sender_rule.action]}{it.sender_rule.client_name ? ` — ${it.sender_rule.client_name}` : ''}</div>}
                  </td>
                  <td style={td}>
                    <div style={ellipsis} title={it.subject_line || ''}>{it.subject_line || '—'}</div>
                    {(it.matched_outlet_name || it.matched_client_name) && <div style={{ ...ellipsis, fontSize: 11, color: 'var(--text-muted)' }}>{it.matched_outlet_name || it.matched_client_name}{it.match_confidence != null ? ` · ${pct(it.match_confidence)}` : ''}</div>}
                    {Array.isArray(it.order_files) && it.order_files.map(fl => (
                      <div key={fl.id}><button onClick={() => onOpenImport?.(fl.id)} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--pld-green)', cursor: 'pointer', fontSize: 12, textDecoration: 'underline', maxWidth: '100%', ...ellipsis }} title={fl.filename}>📎 {fl.filename}</button></div>
                    ))}
                  </td>
                  <td style={td}>
                    <StatusBadge status={it.parse_status === 'REJECTED' ? 'NOT AN ORDER' : it.parse_status} />
                    {(it.parse_status === 'FAILED' || (it.parse_status === 'REJECTED' && /^Not an order|^Ignored/.test(it.parse_error || ''))) && it.parse_error && <div style={{ fontSize: 11, color: it.parse_status === 'FAILED' ? '#991b1b' : 'var(--text-muted)', marginTop: 3, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }} title={it.parse_error}>{it.parse_error}</div>}
                  </td>
                  <td style={{ ...td, textAlign: 'right', position: 'relative' }}>
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                      {['PARSED', 'NEEDS_REVIEW'].includes(it.parse_status) && !(it.order_files?.length) && <Btn size="sm" onClick={() => openReview(it)}>Review</Btn>}
                      {['PENDING', 'FAILED', 'NEEDS_REVIEW'].includes(it.parse_status) && !(it.order_files?.length) && (
                        <Btn size="sm" variant="ghost" disabled={importing === it.id} onClick={() => importAttachments(it)} title="Take in its attachments: known order files go to Order Files; otherwise Blake reads the email again with its attachments">
                          {importing === it.id ? 'Importing…' : 'Import files'}
                        </Btn>
                      )}
                      {!['CONFIRMED', 'REJECTED'].includes(it.parse_status) && <Btn size="sm" onClick={() => markDone([it.id])} title="Dealt with — move it to Done">Done</Btn>}
                      {!['CONFIRMED', 'REJECTED'].includes(it.parse_status) && <Btn size="sm" variant="ghost" onClick={() => dismiss([it.id])} title="Not an order — move it out of the way">Not an order</Btn>}
                      {(it.parse_status === 'REJECTED' || (it.parse_status === 'CONFIRMED' && !it.resolved_order_id)) && <Btn size="sm" variant="ghost" onClick={() => restore(it)} title="Put it back in To deal with">Restore</Btn>}
                      <SenderMenu it={it} clients={clients} onDone={senderSaved} onError={setCheckErr} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Main page
// ═════════════════════════════════════════════════════════════════════════════

const TABS = [
  { key: 'orders', label: 'Fulfilment Orders' },
  { key: 'files', label: 'Order Files' },
  { key: 'email', label: 'Email Queue' },
  { key: 'csv', label: 'CSV Import (old)' },
];

export default function Fulfilment() {
  const [tab, setTab] = useState('orders');
  const [openImportId, setOpenImportId] = useState(null);
  const openImport = (id) => { setOpenImportId(id); setTab('files'); };
  const clearOpenImport = useCallback(() => setOpenImportId(null), []);

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 8px' }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Fulfilment</h1>
        <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: 14 }}>Client orders: import, confirm, despatch, and bill monthly</p>
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '1px solid var(--border)' }}>
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: '10px 16px', fontSize: 13.5, fontWeight: 600, cursor: 'pointer',
              border: 'none', background: 'none',
              color: tab === t.key ? 'var(--pld-green)' : 'var(--text-muted)',
              borderBottom: tab === t.key ? '2px solid var(--pld-green)' : '2px solid transparent',
              marginBottom: -1,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'orders' && <FulfilmentOrdersView />}
      {tab === 'files' && <OrderFilesView openFileId={openImportId} onOpened={clearOpenImport} />}
      {tab === 'csv' && <CsvImportView />}
      {tab === 'email' && <EmailQueueView onOpenImport={openImport} />}
    </div>
  );
}
